/**
 * Join Program Button Block - Frontend View
 *
 * This component hydrates the server-rendered button to add interactivity.
 * The button is already rendered by PHP, so this only adds click handlers and loading states.
 */
import type { FC } from 'react';
import { useState } from '@wordpress/element';
import { createRoot } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import {
	useEnrollment,
	useEnrollmentActions,
} from '../_shared/hooks/use-enrollment';
import type { JoinProgramResponse } from '../_shared/store/enrollment-store';

// ============================================================================
// Types
// ============================================================================

interface JoinProgramButtonAttributes {
	text: string;
	loginText: string;
	enrolledText: string;
}

interface JoinProgramButtonProps {
	attributes: JoinProgramButtonAttributes;
	initialText: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Frontend join program button component
 *
 * Hydrates the server-rendered button to add click handlers and dynamic states.
 */
const JoinProgramButtonView: FC<JoinProgramButtonProps> = ({ attributes, initialText }) => {
	const enrollment = useEnrollment();
	const { setContact, setEnrollmentStatus } = useEnrollmentActions();
	const [isJoining, setIsJoining] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Handle button click
	 */
	const handleClick = async () => {
		// Guest users should login first
		if (enrollment.status === 'guest') {
			// Redirect to login page (WordPress default)
			window.location.href = '/wp-login.php?redirect_to=' + encodeURIComponent(window.location.href);
			return;
		}

		// Already enrolled - shouldn't happen since button is hidden
		if (enrollment.status === 'enrolled') {
			return;
		}

		// Join the program
		setIsJoining(true);
		setError(null);

		try {
			const response = await apiFetch<JoinProgramResponse>({
				path: '/leat/v1/join-program',
				method: 'POST',
			});

			if (response.success && response.contact) {
				// Update enrollment state
				setContact(response.contact);
				setEnrollmentStatus('enrolled');

				// Button will disappear since PHP doesn't render it for enrolled users
				// For now, just show success state briefly before page refresh
				window.location.reload();
			} else {
				setError(response.message || __('Failed to join program', 'leat-crm'));
			}
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : __('Failed to join program', 'leat-crm');
			setError(errorMessage);
		} finally {
			setIsJoining(false);
		}
	};

	/**
	 * Get button text based on state
	 */
	const getButtonText = (): string => {
		if (isJoining) {
			return __('Joining...', 'leat-crm');
		}
		// Use the server-rendered initial text unless we're in a loading/error state
		return initialText;
	};

	const buttonText = getButtonText();

	return (
		<>
			<span
				onClick={handleClick}
				style={{ cursor: isJoining ? 'wait' : 'pointer' }}
			>
				{buttonText}
			</span>
			{error && (
				<div
					className="leat-join-button-error"
					style={{
						color: '#d32f2f',
						fontSize: '14px',
						marginTop: '8px',
						padding: '8px',
					}}
				>
					{error}
				</div>
			)}
		</>
	);
};

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize all join program button blocks on the page
 *
 * The button element is already rendered by PHP inside a wp-block-button wrapper.
 * We just hydrate it to add click handlers and dynamic states.
 */
export function initJoinProgramButtonBlocks(): void {
	// Find all join program button blocks - they have the main block class on the button element
	const buttons = document.querySelectorAll<HTMLButtonElement>('button.wp-block-leat-join-program-button[data-text]');

	for (const button of buttons) {
		// Get attributes from data attributes
		const attributes: JoinProgramButtonAttributes = {
			text: button.dataset.text || '',
			loginText: button.dataset.loginText || '',
			enrolledText: button.dataset.enrolledText || '',
		};

		// Get the initial text from the button's current content
		const initialText = button.textContent || '';

		// Create React root and render into the button element
		const root = createRoot(button);
		root.render(<JoinProgramButtonView attributes={attributes} initialText={initialText} />);
	}
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initJoinProgramButtonBlocks);
} else {
	initJoinProgramButtonBlocks();
}
